import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import MainURL from '../MainURL';

// 재정관리 콘솔 공용 API. 모든 재정 API는 서버가 requesterId의 재정 권한 등급을 매 요청 재검증한다
// (server/routers/appAdmin/finance/*). 권한 등급: manager(재정부장) > clerk(재정부원) > viewer(조회).

export type FinanceRole = 'manager' | 'clerk' | 'viewer';

export type FinanceCtx = {
  authChurch: string;
  requesterId: number;
  role: FinanceRole;
};

export const ROLE_LABEL: Record<FinanceRole, string> = {
  manager: '재정부장',
  clerk: '재정부원',
  viewer: '조회 전용',
};

export const canWrite = (ctx: FinanceCtx) => ctx.role !== 'viewer';
export const isManager = (ctx: FinanceCtx) => ctx.role === 'manager';

const base = `${MainURL}/finance`;

export async function financeGet<T>(ctx: FinanceCtx, path: string, params: Record<string, unknown> = {}): Promise<T> {
  const res = await axios.get(`${base}${path}`, {
    params: { authChurch: ctx.authChurch, requesterId: ctx.requesterId, ...params },
  });
  return res.data?.data as T;
}

export async function financeSend<T = unknown>(
  ctx: FinanceCtx,
  method: 'post' | 'put',
  path: string,
  body: Record<string, unknown> = {},
): Promise<T> {
  const res = await axios[method](`${base}${path}`, { authChurch: ctx.authChurch, requesterId: ctx.requesterId, ...body });
  return res.data as T;
}

export async function financeDelete<T = unknown>(ctx: FinanceCtx, path: string): Promise<T> {
  const res = await axios.delete(`${base}${path}`, { params: { authChurch: ctx.authChurch, requesterId: ctx.requesterId } });
  return res.data as T;
}

export async function financeUpload(ctx: FinanceCtx, path: string, files: File[]) {
  const form = new FormData();
  form.append('authChurch', ctx.authChurch);
  form.append('requesterId', String(ctx.requesterId));
  files.forEach((file) => form.append('files', file));
  const res = await axios.post(`${base}${path}`, form);
  return res.data;
}

/** 권한을 확인하는 GET 주소(새 창으로 여는 증빙 파일 등). */
export function financeFileUrl(ctx: FinanceCtx, path: string) {
  const qs = new URLSearchParams({ authChurch: ctx.authChurch, requesterId: String(ctx.requesterId) });
  return `${base}${path}?${qs.toString()}`;
}

export function errorMessage(err: unknown, fallback: string) {
  if (axios.isAxiosError(err)) {
    const data = err.response?.data;
    if (data?.code === 'FINANCE_NOT_ENABLED') return '이 교회는 재정 서비스를 사용하지 않습니다. 플랫폼 운영사에 문의해 주세요.';
    if (data?.code === 'NO_FINANCE_ACCESS') return '재정관리 권한이 없습니다. 교회 최종관리자에게 재정 권한을 요청해 주세요.';
    if (typeof data?.message === 'string' && data.message) return data.message;
  }
  return fallback;
}

// ---- 표시 도우미 ------------------------------------------------------------------------

export function won(amount: number) {
  return `${Math.round(amount).toLocaleString('ko-KR')}원`;
}

export function num(amount: number) {
  return Math.round(amount).toLocaleString('ko-KR');
}

export function pad2(n: number) {
  return String(n).padStart(2, '0');
}

export function todayValue() {
  const d = new Date();
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

export function currentMonthValue() {
  return todayValue().slice(0, 7);
}

export function monthRange(month: string) {
  const [y, m] = month.split('-').map(Number);
  const last = new Date(y, m, 0).getDate();
  return { from: `${month}-01`, to: `${month}-${pad2(last)}` };
}

export function percent(part: number, whole: number) {
  return whole > 0 ? Math.round((part / whole) * 1000) / 10 : 0;
}

/** 예산 집행률 상태: 90% 이상 경고, 100% 초과 초과. */
export function executionTone(rate: number, hasBudget: boolean): 'none' | 'ok' | 'warn' | 'over' {
  if (!hasBudget) return 'none';
  if (rate > 100) return 'over';
  if (rate >= 90) return 'warn';
  return 'ok';
}

// ---- 데이터 타입 -----------------------------------------------------------------------

export type FinanceRecord = {
  id: number;
  recordDate: string;
  type: '수입' | '지출';
  category: string;
  amount: number;
  status: string;
  memo: string;
  memberId: number | null;
  donorName: string;
  anonymous: number;
  department: string;
  method: string;
  source: string;
  expenseRequestId: number | null;
};

export type FinanceCode = {
  id: number;
  kind: 'income' | 'expense' | 'department';
  name: string;
  sortOrder: number;
  active: number;
  receiptable: number;
};

export type FinanceMember = {
  id: number;
  name: string;
  phone: string;
  birthDate: string | null;
  department: string;
  status: string;
};

export type FinanceSettings = {
  churchName: string;
  registrationNo: string;
  representative: string;
  address: string;
  phone: string;
  openingBalance: number;
  openingDate: string | null;
};

export const PAYMENT_METHODS = ['현금', '계좌이체', '카드', '수표', '기타'];

export const SOURCE_LABEL: Record<string, string> = {
  manual: '개별 입력',
  excel: '엑셀 일괄',
  bank: '통장 매칭',
  expense: '지출 결의서',
  app: '재정 앱',
};

/** 교회 재정 코드(헌금 항목·계정과목·부서). 사용 중지된 코드는 선택지에서 뺀다. */
export function useFinanceCodes(ctx: FinanceCtx) {
  const [codes, setCodes] = useState<FinanceCode[]>([]);
  const [loaded, setLoaded] = useState(false);

  const reload = useCallback(() => {
    financeGet<FinanceCode[]>(ctx, '/codes')
      .then((data) => setCodes(data ?? []))
      .catch((err) => console.error('GET /finance/codes', err))
      .finally(() => setLoaded(true));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ctx.authChurch, ctx.requesterId]);

  useEffect(() => {
    reload();
  }, [reload]);

  const names = (kind: FinanceCode['kind']) => codes.filter((c) => c.kind === kind && c.active).map((c) => c.name);

  return {
    codes,
    loaded,
    reload,
    incomeCategories: names('income'),
    expenseCategories: names('expense'),
    departments: names('department'),
  };
}

/** 선택지에 없는 기존 값(예전 항목명 등)도 수정 화면에서 사라지지 않게 붙여 준다. */
export function withCurrent(options: string[], current: string) {
  return current && !options.includes(current) ? [...options, current] : options;
}

export function departmentLabel(department: string) {
  return department || '공통';
}
